// SettingsLicence.jsx - Gestion des modèles de licence
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { APP_CONFIG } from '../../../config/appConfig';

export default function SettingsLicence() {
    const { token } = useAuth();
    const [licences, setLicences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingLicence, setEditingLicence] = useState(null);

    // Formulaire avec les bons champs
    const [formData, setFormData] = useState({
        modellicence: '',
        licencetype: 'MONTH',      // TypeLicence: MONTH, YEAR, SEMESTER, TRIMESTRE, WEEK, DAY, HOUR
        typeModele: 'STANDARD',    // STANDARD, PREMIUM, ENTERPRISE, TRIAL
        maxEtudiants: 100,
        maxFormations: 10,
        maxClients: 5,
        maxAdmins: 3,
        dureeMois: 12,
        prix: 0,
        status: true,
        description: ''
    });

    // Récupération des licences
    const fetchLicences = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/licences`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Session expirée');
                if (response.status === 403) throw new Error('Accès refusé');
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            const data = await response.json();
            setLicences(data.data || data || []);
        } catch (err) {
            console.error("Erreur récupération licences:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchLicences();
    }, [fetchLicences]);

    // Création d'une licence
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                modellicence: formData.modellicence,
                licencetype: formData.licencetype,
                typeModele: formData.typeModele,
                maxEtudiants: formData.maxEtudiants,
                maxFormations: formData.maxFormations,
                maxClients: formData.maxClients,
                maxAdmins: formData.maxAdmins,
                dureeMois: formData.dureeMois,
                prix: formData.prix,
                status: formData.status,
                description: formData.description
            };

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/licences`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchLicences();
                alert("✅ Modèle de licence créé avec succès");
            } else {
                setError(data.error || "Erreur lors de la création");
                alert(`❌ ${data.error || "Erreur lors de la création"}`);
            }
        } catch (err) {
            console.error("Erreur création:", err);
            setError("Erreur technique lors de la création");
            alert("❌ Erreur technique lors de la création");
        } finally {
            setLoading(false);
        }
    };

    // Mise à jour d'une licence
    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                modellicence: formData.modellicence,
                licencetype: formData.licencetype,
                typeModele: formData.typeModele,
                maxEtudiants: formData.maxEtudiants,
                maxFormations: formData.maxFormations,
                maxClients: formData.maxClients,
                maxAdmins: formData.maxAdmins,
                dureeMois: formData.dureeMois,
                prix: formData.prix,
                status: formData.status,
                description: formData.description
            };

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/licences/${editingLicence.id}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                setShowEditModal(false);
                setEditingLicence(null);
                resetForm();
                fetchLicences();
                alert("✅ Modèle de licence modifié avec succès");
            } else {
                setError(data.error || "Erreur lors de la modification");
                alert(`❌ ${data.error || "Erreur lors de la modification"}`);
            }
        } catch (err) {
            console.error("Erreur modification:", err);
            setError("Erreur technique lors de la modification");
            alert("❌ Erreur technique lors de la modification");
        } finally {
            setLoading(false);
        }
    };

    // Suppression d'une licence
    const handleDelete = async (licence) => {
        if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer le modèle "${licence.modellicence}" ?`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/licences/${licence.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                fetchLicences();
                alert("✅ Modèle de licence supprimé avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la suppression");
                alert(`❌ ${data.error || "Erreur lors de la suppression"}`);
            }
        } catch (err) {
            console.error("Erreur suppression:", err);
            setError("Erreur technique lors de la suppression");
            alert("❌ Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    // Activation/Désactivation
    const handleToggleStatus = async (licence) => {
        const newStatus = !licence.status;
        const action = newStatus ? 'activer' : 'désactiver';

        if (!window.confirm(`Êtes-vous sûr de vouloir ${action} le modèle "${licence.modellicence}" ?`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/config/licences/${licence.id}/status?actif=${newStatus}`,
                {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.ok) {
                fetchLicences();
                alert(`✅ Modèle ${action} avec succès`);
            } else {
                const data = await response.json();
                setError(data.error || `Erreur lors de l'${action}`);
                alert(`❌ ${data.error || `Erreur lors de l'${action}`}`);
            }
        } catch (err) {
            console.error("Erreur changement statut:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // Ouvrir le modal de modification
    const openEditModal = (licence) => {
        setEditingLicence(licence);
        setFormData({
            modellicence: licence.modellicence || '',
            licencetype: licence.licencetype || 'MONTH',
            typeModele: licence.typeModele || 'STANDARD',
            maxEtudiants: licence.maxEtudiants || 100,
            maxFormations: licence.maxFormations || 10,
            maxClients: licence.maxClients || 5,
            maxAdmins: licence.maxAdmins || 3,
            dureeMois: licence.dureeMois || 12,
            prix: licence.prix || 0,
            status: licence.status !== undefined ? licence.status : true,
            description: licence.description || ''
        });
        setShowEditModal(true);
    };

    // Réinitialiser le formulaire
    const resetForm = () => {
        setFormData({
            modellicence: '',
            licencetype: 'MONTH',
            typeModele: 'STANDARD',
            maxEtudiants: 100,
            maxFormations: 10,
            maxClients: 5,
            maxAdmins: 3,
            dureeMois: 12,
            prix: 0,
            status: true,
            description: ''
        });
        setError(null);
    };

    // Gestion des entrées
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : 
                    type === 'number' ? parseInt(value) || 0 : value
        }));
    };

    // Affichage du statut
    const getStatusBadge = (status) => {
        if (status === undefined || status === null) return null;
        return status ? (
            <span className="px-2.5 py-0.5 rounded-full text-[9px] font-serif font-bold bg-[#e8f5ed] text-[#009640] uppercase tracking-wider">
                Actif
            </span>
        ) : (
            <span className="px-2.5 py-0.5 rounded-full text-[9px] font-serif font-bold bg-[#fef3e8] text-[#ef7c00] uppercase tracking-wider">
                Inactif
            </span>
        );
    };

    // Formatage du type de modèle (STANDARD, PREMIUM, etc.)
    const getTypeModeleBadge = (typeModele) => {
        const colors = {
            'STANDARD': 'bg-[#e8f5ed] text-[#009640]',
            'PREMIUM': 'bg-[#fef3e8] text-[#ef7c00]',
            'ENTERPRISE': 'bg-[#dbeafe] text-[#1d4ed8]',
            'TRIAL': 'bg-[#f8f9fa] text-[#6c757d]'
        };
        const labels = {
            'STANDARD': 'Standard',
            'PREMIUM': 'Premium',
            'ENTERPRISE': 'Entreprise',
            'TRIAL': 'Essai'
        };
        return (
            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-serif font-bold uppercase tracking-wider ${colors[typeModele] || colors.STANDARD}`}>
                {labels[typeModele] || typeModele}
            </span>
        );
    };

    // Formatage de la durée
    const getDureeLabel = (licencetype) => {
        const labels = {
            'MONTH': 'Mensuelle',
            'YEAR': 'Annuelle',
            'SEMESTER': 'Semestrielle',
            'TRIMESTRE': 'Trimestrielle',
            'WEEK': 'Hebdomadaire',
            'DAY': 'Journalière',
            'HOUR': 'Horaire'
        };
        return labels[licencetype] || licencetype;
    };

    if (!token) {
        return (
            <div className="p-6 text-center font-serif">
                <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-xl p-4">
                    <p className="text-[#212529] font-serif">Authentification requise pour gérer les licences</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5 text-left font-serif">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-[#e9ecef] pb-4">
                <div>
                    <h2 className="text-2xl font-serif font-bold text-[#212529] tracking-tight">
                        Modèles de Licence
                    </h2>
                    <p className="text-sm text-[#6c757d] font-serif">
                        Création et gestion des modèles de licence pour les administrateurs
                    </p>
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                    className="px-5 py-2.5 rounded-xl text-sm font-serif font-semibold flex items-center gap-2 bg-[#009640] hover:bg-[#007a33] text-white shadow-md hover:shadow-lg transition-all duration-200"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Nouveau modèle
                </button>
            </div>

            {/* Message d'erreur */}
            {error && (
                <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-xl p-3 text-[#212529] text-sm font-serif">
                    <strong>Erreur :</strong> {error}
                </div>
            )}

            {/* Liste des licences */}
            <div className="bg-white rounded-2xl shadow-sm border border-[#e9ecef] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-b border-[#e9ecef]">
                            <tr>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider">
                                    Nom / Type
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Étudiants
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Formations
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Clients
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Durée
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Prix (FCFA)
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Statut
                                </th>
                                <th className="px-5 py-3.5 text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider text-center">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#e9ecef]">
                            {loading && licences.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-5 py-8 text-center">
                                        <div className="flex justify-center">
                                            <svg className="animate-spin h-6 w-6" style={{ color: '#009640' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                        <p className="text-xs text-[#6c757d] mt-2 font-serif">Chargement...</p>
                                    </td>
                                </tr>
                            ) : licences.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-5 py-8 text-center">
                                        <p className="text-sm text-[#adb5bd] font-serif">Aucun modèle de licence trouvé</p>
                                    </td>
                                </tr>
                            ) : (
                                licences.map(licence => (
                                    <tr key={licence.id} className="hover:bg-[#f8f9fa] transition-colors">
                                        <td className="px-5 py-3.5">
                                            <div className="text-sm font-serif font-bold text-[#212529]">
                                                {licence.modellicence}
                                            </div>
                                            <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                                                {getTypeModeleBadge(licence.typeModele)}
                                                <span className="text-[10px] text-[#6c757d] font-serif">
                                                    • {getDureeLabel(licence.licencetype)}
                                                </span>
                                            </div>
                                            {licence.description && (
                                                <div className="text-[10px] font-serif text-[#6c757d] mt-0.5 italic">
                                                    {licence.description}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5 text-center text-sm font-serif font-bold text-[#212529]">
                                            {licence.maxEtudiants}
                                        </td>
                                        <td className="px-5 py-3.5 text-center text-sm font-serif font-bold text-[#212529]">
                                            {licence.maxFormations}
                                        </td>
                                        <td className="px-5 py-3.5 text-center text-sm font-serif font-bold text-[#212529]">
                                            {licence.maxClients}
                                        </td>
                                        <td className="px-5 py-3.5 text-center text-sm font-serif text-[#495057]">
                                            {licence.dureeMois} mois
                                        </td>
                                        <td className="px-5 py-3.5 text-center text-sm font-serif font-bold text-[#009640]">
                                            {licence.prix?.toLocaleString() || 0}
                                        </td>
                                        <td className="px-5 py-3.5 text-center">
                                            {getStatusBadge(licence.status)}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <button
                                                    onClick={() => openEditModal(licence)}
                                                    className="p-2 rounded-lg hover:bg-[#e8f5ed] transition-colors group"
                                                    title="Modifier"
                                                >
                                                    <svg className="w-4 h-4 text-[#6c757d] group-hover:text-[#009640] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleToggleStatus(licence)}
                                                    className={`p-2 rounded-lg transition-colors group ${licence.status ? 'hover:bg-[#fef3e8]' : 'hover:bg-[#e8f5ed]'}`}
                                                    title={licence.status ? 'Désactiver' : 'Activer'}
                                                >
                                                    {licence.status ? (
                                                        <svg className="w-4 h-4 text-[#6c757d] group-hover:text-[#ef7c00] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4 text-[#6c757d] group-hover:text-[#009640] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(licence)}
                                                    className="p-2 rounded-lg hover:bg-[#f8d7da] transition-colors group"
                                                    title="Supprimer"
                                                >
                                                    <svg className="w-4 h-4 text-[#6c757d] group-hover:text-[#dc3545] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL : CRÉATION D'UNE LICENCE */}
            {showModal && (
                <div className="fixed inset-0 bg-[#212529]/40 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl border border-[#e9ecef] max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-serif font-bold text-[#212529] mb-5 flex items-center gap-2">
                            <span className="text-[#009640]">✦</span>
                            Nouveau modèle de licence
                        </h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Nom du modèle *
                                    </label>
                                    <input
                                        type="text"
                                        name="modellicence"
                                        placeholder="Ex: Licence Premium"
                                        required
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.modellicence}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Type de modèle *
                                    </label>
                                    <select
                                        name="typeModele"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.typeModele}
                                        onChange={handleInputChange}
                                    >
                                        <option value="STANDARD">Standard</option>
                                        <option value="PREMIUM">Premium</option>
                                        <option value="ENTERPRISE">Entreprise</option>
                                        <option value="TRIAL">Essai</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Périodicité *
                                    </label>
                                    <select
                                        name="licencetype"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.licencetype}
                                        onChange={handleInputChange}
                                    >
                                        <option value="MONTH">Mensuelle</option>
                                        <option value="YEAR">Annuelle</option>
                                        <option value="SEMESTER">Semestrielle</option>
                                        <option value="TRIMESTRE">Trimestrielle</option>
                                        <option value="WEEK">Hebdomadaire</option>
                                        <option value="DAY">Journalière</option>
                                        <option value="HOUR">Horaire</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Durée (mois)
                                    </label>
                                    <input
                                        type="number"
                                        name="dureeMois"
                                        min="1"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.dureeMois}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    placeholder="Description du modèle de licence"
                                    rows="2"
                                    className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all resize-none"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Étudiants
                                    </label>
                                    <input
                                        type="number"
                                        name="maxEtudiants"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxEtudiants}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Formations
                                    </label>
                                    <input
                                        type="number"
                                        name="maxFormations"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxFormations}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Clients
                                    </label>
                                    <input
                                        type="number"
                                        name="maxClients"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxClients}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Admins
                                    </label>
                                    <input
                                        type="number"
                                        name="maxAdmins"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxAdmins}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Prix (FCFA)
                                    </label>
                                    <input
                                        type="number"
                                        name="prix"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.prix}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <input
                                    type="checkbox"
                                    name="status"
                                    id="licenceActif"
                                    checked={formData.status}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 rounded border-[#e9ecef] text-[#009640] focus:ring-[#009640] focus:ring-2"
                                />
                                <label htmlFor="licenceActif" className="text-sm font-serif text-[#495057]">
                                    Activer ce modèle immédiatement
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-[#e9ecef]">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 px-4 py-2.5 text-sm font-serif font-semibold text-[#6c757d] bg-[#f8f9fa] rounded-xl hover:bg-[#e9ecef] transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 px-4 py-2.5 text-sm font-serif font-semibold text-white rounded-xl bg-[#009640] hover:bg-[#007a33] transition-colors shadow-md hover:shadow-lg disabled:opacity-60"
                                >
                                    {loading ? 'Création...' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL : MODIFICATION D'UNE LICENCE */}
            {showEditModal && editingLicence && (
                <div className="fixed inset-0 bg-[#212529]/40 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl border border-[#e9ecef] max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-serif font-bold text-[#212529] mb-5 flex items-center gap-2">
                            <span className="text-[#009640]">✎</span>
                            Modifier le modèle de licence
                        </h3>
                        <form onSubmit={handleUpdate} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Nom du modèle *
                                    </label>
                                    <input
                                        type="text"
                                        name="modellicence"
                                        placeholder="Ex: Licence Premium"
                                        required
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.modellicence || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Type de modèle *
                                    </label>
                                    <select
                                        name="typeModele"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.typeModele || 'STANDARD'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="STANDARD">Standard</option>
                                        <option value="PREMIUM">Premium</option>
                                        <option value="ENTERPRISE">Entreprise</option>
                                        <option value="TRIAL">Essai</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Périodicité *
                                    </label>
                                    <select
                                        name="licencetype"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.licencetype || 'MONTH'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="MONTH">Mensuelle</option>
                                        <option value="YEAR">Annuelle</option>
                                        <option value="SEMESTER">Semestrielle</option>
                                        <option value="TRIMESTRE">Trimestrielle</option>
                                        <option value="WEEK">Hebdomadaire</option>
                                        <option value="DAY">Journalière</option>
                                        <option value="HOUR">Horaire</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Durée (mois)
                                    </label>
                                    <input
                                        type="number"
                                        name="dureeMois"
                                        min="1"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.dureeMois || 12}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    placeholder="Description du modèle de licence"
                                    rows="2"
                                    className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all resize-none"
                                    value={formData.description || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Étudiants
                                    </label>
                                    <input
                                        type="number"
                                        name="maxEtudiants"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxEtudiants || 0}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Formations
                                    </label>
                                    <input
                                        type="number"
                                        name="maxFormations"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxFormations || 0}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Clients
                                    </label>
                                    <input
                                        type="number"
                                        name="maxClients"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxClients || 0}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Max Admins
                                    </label>
                                    <input
                                        type="number"
                                        name="maxAdmins"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.maxAdmins || 0}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                        Prix (FCFA)
                                    </label>
                                    <input
                                        type="number"
                                        name="prix"
                                        min="0"
                                        className="w-full px-4 py-2.5 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-xl focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        value={formData.prix || 0}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <input
                                    type="checkbox"
                                    name="status"
                                    id="editLicenceActif"
                                    checked={formData.status !== undefined ? formData.status : true}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 rounded border-[#e9ecef] text-[#009640] focus:ring-[#009640] focus:ring-2"
                                />
                                <label htmlFor="editLicenceActif" className="text-sm font-serif text-[#495057]">
                                    Modèle actif
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-[#e9ecef]">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingLicence(null);
                                        resetForm();
                                    }}
                                    className="flex-1 px-4 py-2.5 text-sm font-serif font-semibold text-[#6c757d] bg-[#f8f9fa] rounded-xl hover:bg-[#e9ecef] transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 px-4 py-2.5 text-sm font-serif font-semibold text-white rounded-xl bg-[#009640] hover:bg-[#007a33] transition-colors shadow-md hover:shadow-lg disabled:opacity-60"
                                >
                                    {loading ? 'Mise à jour...' : 'Mettre à jour'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}